import { type PatchTestData } from '../../lib/messaging-api';

const formatWhen = (iso: string): string => {
  try {
    // Always render in UK time — the booking system is UK-based, so the card
    // must match the backend's UK-formatted chat message (and the pro's
    // selection) regardless of the viewer's browser timezone.
    // 12-hour with an AM/PM indicator so it matches the time format the pro
    // picks (they choose in 12-hour AM/PM; a bare 24-hour readout confused
    // users — Issue 2b).
    return new Date(iso)
      .toLocaleString('en-GB', {
        timeZone: 'Europe/London',
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
  } catch {
    return iso;
  }
};

const STATUS_STYLE: Record<string, string> = {
  PROPOSED: 'bg-[#fff7e6] text-[#9a6700] border-[#fde2a1]',
  CONFIRMED: 'bg-[#e7f6ec] text-[#1b7a3d] border-[#bfe6cb]',
  DECLINED: 'bg-[#fde8e8] text-[#b42318] border-[#f3b4b4]',
  CANCELLED: 'bg-[#f3f3f3] text-[#908e8e] border-[#e5e5e5]',
};
const STATUS_LABEL: Record<string, string> = {
  PROPOSED: 'Awaiting confirmation',
  CONFIRMED: 'Confirmed',
  DECLINED: 'Declined',
  CANCELLED: 'Cancelled',
};

/**
 * Renders a PATCH_TEST_REQUEST chat bubble. The client (recipient) sees
 * Confirm / Decline while PROPOSED; the pro (creator) sees a Cancel link.
 */
export function PatchTestBubble({
  pt,
  fromMe,
  canRespond,
  onConfirm,
  onDecline,
  onCancel,
  busy,
}: {
  pt: PatchTestData;
  // true if the current user proposed it (the pro)
  fromMe: boolean;
  // true if the current user is the client who can confirm/decline
  canRespond: boolean;
  onConfirm?: () => void;
  onDecline?: () => void;
  onCancel?: () => void;
  busy?: boolean;
}) {
  const isProposed = pt.status === 'PROPOSED';
  const isConfirmed = pt.status === 'CONFIRMED';

  return (
    <div className="w-[280px] max-w-full rounded-2xl border border-[#ede8e9] bg-white text-[#3a3335] overflow-hidden">
      <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-[#f3eee9]">
        <span className="material-icons text-[#8e2a2f] text-[20px]">
          event_available
        </span>
        <span className="font-bold text-[13px]">Patch test</span>
        <span
          className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${
            STATUS_STYLE[pt.status] ?? STATUS_STYLE.CANCELLED
          }`}
        >
          {STATUS_LABEL[pt.status] ?? pt.status}
        </span>
      </div>

      <div className="px-4 py-3">
        <p className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px]">
          {formatWhen(pt.scheduled_start)}
        </p>
        <p className="mt-0.5 text-[12px] text-[#908e8e]">
          {pt.duration_minutes} min
          {pt.service_name ? ` · for ${pt.service_name}` : ''}
        </p>
        {pt.note ? (
          <p className="mt-2 text-[13px] break-words">{pt.note}</p>
        ) : null}

        {isProposed && canRespond && onConfirm ? (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="flex-1 h-[40px] rounded-[10px] bg-[#8e2a2f] text-white text-[14px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
            >
              {busy ? '…' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={onDecline}
              disabled={busy}
              className="flex-1 h-[40px] rounded-[10px] border border-[#ede8e9] text-[#908e8e] text-[14px] font-bold hover:border-[#dc2626] hover:text-[#dc2626] transition-colors disabled:opacity-60"
            >
              Decline
            </button>
          </div>
        ) : null}

        {(isProposed || isConfirmed) && fromMe && onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="mt-2 w-full h-[36px] rounded-[10px] border border-[#ede8e9] text-[#908e8e] text-[13px] font-bold hover:border-[#dc2626] hover:text-[#dc2626] transition-colors disabled:opacity-60"
          >
            {busy ? '…' : 'Cancel patch test'}
          </button>
        ) : null}

        {isConfirmed ? (
          <p className="mt-2 text-[11px] text-[#1b7a3d] font-medium">
            Added to both calendars. Kept separate from the main treatment.
          </p>
        ) : null}
      </div>
    </div>
  );
}
